import { clsx } from 'clsx';
import moment from 'moment-timezone';
import { Col, Form, Row } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { CreateProps, Create, useNotify, useRedirect } from 'react-admin';

import { axiosErrorToString } from '@utils';
import { creditHttp, financeAccountHttp, financeWithdrawalHttp, ordersHttp } from '@network';
import { useCreateProps, useQuery, useRbacCreate } from '@hooks';
import { Container, Forbidden, Loader, PageHeader } from '@components';
import {
  CreateFinanceAccountParams,
  CreateFinanceWithdrawalParams,
  CreditInvoiceFile,
  FinanceWithdrawalAccountOption,
  FinanceWithdrawalOptions,
  Order,
} from '@types';
import { FinanceWithdrawalForm } from '../FinanceWithdrawalForm';
import cls from './finance-withdrawal-create.module.css';

export const FinanceWithdrawalCreate = (props: CreateProps) => {
  const query = useQuery();
  const notify = useNotify();
  const redirect = useRedirect();
  const rbacCreate = useRbacCreate();
  const createProps = useCreateProps();
  const [options, setOptions] = useState<FinanceWithdrawalOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<CreateFinanceWithdrawalParams>();
  const [submitted, setSubmitted] = useState(false);
  const [clientName, setClientName] = useState('');
  const [invoiceDocument, setInvoiceDocument] = useState<CreditInvoiceFile | null>(null);
  const [initialAccount, setInitialAccount] = useState<FinanceWithdrawalAccountOption | null>(null);
  const [prefilledOrder, setPrefilledOrder] = useState<Order | null>(null);
  const [invoiceOrderId, setInvoiceOrderId] = useState<number | null>(null);
  const isFinanceAccountCreating = useRef(false);
  const accountId = query.get('id');
  const invoiceId = query.get('invoiceId');
  const orderId = query.get('orderId');

  const formValues: CreateFinanceWithdrawalParams = {
    concept: '',
    accountId: accountId && options?.accounts.find(a => a.id === +accountId) ? +accountId : null,
    creditId: null,
    amount: 0,
    paymentDate: moment(new Date()).format('DD-MM-YYYY'),
    purpose: '',
    details: null,
    sent: false,
    received: false,
    fee: false,
    feePercent: null,
    isUsd: false,
    exchangeRate: 0,
    amountUsd: 0,
  };

  const getOptions = async () => {
    try {
      const ops: FinanceWithdrawalOptions = await financeWithdrawalHttp.options();

      if (!ops) {
        throw new Error('The list of options has not been received');
      }
      setOptions(ops);
    } catch (e) {
      console.error(e);
    }
  };

  const submit = async (params: CreateFinanceWithdrawalParams) => {
    if (loading) return;
    try {
      setLoading(true);
      const newWithdrawal = await financeWithdrawalHttp.create({
        ...params,
        isOrderPrefilled: !!prefilledOrder,
      });

      form.resetFields();
      setClientName('');
      setSubmitted(false);

      notify('Withdrawal has been created');
      redirect(`/finance-withdrawal/${newWithdrawal.id}`);
    } catch (e) {
      // @ts-ignore
      notify(axiosErrorToString(e), { type: 'error' });
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await getOptions();
    })();
  }, []);

  useEffect(() => {
    if (!invoiceId) return;

    (async () => {
      const invoice = await creditHttp.getInvoiceById(+invoiceId);
      setInvoiceDocument(invoice.file);

      form.setFieldValue('concept', 'RI');
      form.setFieldValue('uuid', invoice.xmlUuid);
      form.setFieldValue('creditId', invoice.creditId);
      form.setFieldValue('amount', invoice.total);
      form.setFieldValue('purpose', 'Operations');
      form.setFieldValue('fee', 'checked');

      formValues.paymentDate = moment(new Date()).toISOString();
      formValues.concept = 'RI';

      setClientName(invoice.invoiceAccredited || '');

      if (invoice.user.financeAccount) {
        const { financeAccount } = invoice.user;
        setInitialAccount({
          id: financeAccount.id,
          code: financeAccount?.code,
          account: financeAccount?.account,
          balance: financeAccount?.balance,
        });
        form.setFieldValue('accountId', invoice.user?.financeAccount?.id || null);
      }

      if (invoice.order?.id) {
        setInvoiceOrderId(invoice.order.id);
      }
    })();
  }, [invoiceId]);

  useEffect(() => {
    if (!orderId) return;

    (async () => {
      const order = await ordersHttp.getOrder(+orderId);
      setPrefilledOrder(order);

      let financeAccount = order.user?.financeAccount || null;

      if (!order.user?.financeAccount && !isFinanceAccountCreating.current) {
        isFinanceAccountCreating.current = true;

        const { user, credit } = order;
        if (user) {
          const createFinanceAccountParams: CreateFinanceAccountParams = {
            userId: user.id,
            userCode: user.code,
            code: `${order.credit?.code} ${order.credit?.contract?.accountHolder}` || '',
            addressee: user.fullName || credit?.contract?.userAddress || '',
            bank: credit?.contract?.accreditedBank || '',
            type: 'Agricultor',
            account: credit?.contract?.clabe || '',
            branch: '',
            reference: null,
            phone: '',
            email: '',
            accountSetup: false,
            balance: 0,
          };

          financeAccount = await financeAccountHttp.create(createFinanceAccountParams);
        }
      }

      formValues.paymentDate = moment(new Date()).toISOString();
      form.setFieldValue('concept', 'RI');
      form.setFieldValue('purpose', 'Operations');
      form.setFieldValue('fee', 'checked');
      form.setFieldValue('creditId', order.creditId);
      form.setFieldValue('amount', order.totalCost);

      if (financeAccount) {
        setInitialAccount({
          id: financeAccount.id,
          code: financeAccount?.code,
          account: financeAccount?.account,
          balance: financeAccount?.balance,
        });
        form.setFieldValue('accountId', financeAccount.id || null);
      }
    })();
  }, [orderId]);

  if (!options) {
    return <Loader center />;
  }

  if (!rbacCreate) {
    return <Forbidden />;
  }

  return (
    <Create {...props} {...createProps} className={clsx(cls.page, 'details-page-wrapper')}>
      <PageHeader
        title="Withdrawal"
        linkText="Back to all withdrawals"
        linkUrl="/finance-withdrawal"
      />

      <Container max>
        <Row gutter={24} className={cls.wrapper}>
          <Col xs={24} className={cls.wrapperCol}>
            <div className={cls.block}>
              <div className={cls.blockHeader}>Complete information</div>
              <Form
                form={form}
                layout="horizontal"
                initialValues={formValues}
              >
                <FinanceWithdrawalForm
                  loading={loading}
                  setLoading={setLoading}
                  initial={formValues}
                  options={options}
                  onSubmit={submit}
                  form={form}
                  clientName={clientName}
                  setClientName={setClientName}
                  submitted={submitted}
                  setSubmitted={setSubmitted}
                  invoiceDocument={invoiceDocument}
                  invoiceOrderId={invoiceOrderId}
                  initialAccount={initialAccount}
                  prefilledOrder={orderId ? prefilledOrder : null}
                />
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </Create>
  );
};
